import React, { Fragment, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { FaPen, FaPlus, FaSearch, FaImage, FaSpinner, FaFolder, FaTrash } from "react-icons/fa";
import LoadingScreen from '../../components/LoadingScreen';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3500';

function CategoryPage() {
    const [categoryList, setCategoryList] = useState([]);
    const [filteredCategories, setFilteredCategories] = useState([]);
    const [categoryData, setCategoryData] = useState({ name: '', image: '' });
    const [imageUploading, setImageUploading] = useState(false);
    const [savingCategory, setSavingCategory] = useState(false);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [loadedImages, setLoadedImages] = useState({});
    const token = localStorage.getItem('authToken');

    useEffect(() => {
        handleCategoryList();
    }, []);

    // Fetch all categories
    const handleCategoryList = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/categorydata`);
            const categories = response.data || [];
            setCategoryList(categories);
            setFilteredCategories(categories);
        } catch (error) {
            alert("Failed to fetch categories. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Search category with debouncing
    const handleSearchCategory = (event) => {
        const search = event.target.value.toLowerCase();
        setSearchTerm(search);
        
        if (search) {
            const filtered = categoryList.filter(category => 
                category.name.toLowerCase().includes(search)
            );
            setFilteredCategories(filtered);
        } else {
            setFilteredCategories(categoryList);
        }
    };

    // Handle image upload
    const handleImageUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            alert('Only JPG, PNG, or WebP images are allowed.');
            return;
        }

        if (file.size > 5 * 1024 * 1024) { // 5MB limit
            alert('File size must be under 5MB.');
            return;
        }

        try {
            setImageUploading(true);
            const formData = new FormData();
            formData.append('image', file);
            const response = await axios.post(`${API_BASE_URL}/upload-prod-img`, formData);
            setCategoryData({ ...categoryData, image: response.data.path });
            alert("Image uploaded successfully!");
        } catch (error) {
            alert("Image upload failed. Please try again.");
        } finally {
            setImageUploading(false);
        }
    };

    // Handle form input changes
    const handleChange = (event) => {
        setCategoryData({ ...categoryData, [event.target.name]: event.target.value });
    };

    // Validate input fields
    const validateFields = () => {
        if (!categoryData.name.trim()) {
            alert("Category name is required.");
            return false;
        }
        if (!categoryData.image.trim()) {
            alert("Category image is required.");
            return false;
        }
        return true;
    };

    // Add a new category
    const handleAddCategory = async () => {
        if (!validateFields()) return;

        try {
            setSavingCategory(true);
            const response = await axios.post(`${API_BASE_URL}/category`, categoryData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert(response.data.message);
            handleCategoryList();
            setCategoryData({ name: '', image: '' });
            // Close modal using data-bs-dismiss attribute
            const closeButton = document.querySelector('[data-bs-dismiss="modal"]');
            if (closeButton) {
                closeButton.click();
            }
        } catch (error) {
            alert("Failed to add category. Please try again.");
        } finally {
            setSavingCategory(false);
        }
    };

    // Delete a category
    const handleDeleteCategory = async (id) => {
        if (!window.confirm("Are you sure you want to delete this category?")) return;

        try {
            await axios.delete(`${API_BASE_URL}/delete-category/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert("Category deleted successfully!");
            handleCategoryList();
        } catch (error) {
            alert("Failed to delete category. Please try again.");
        }
    };

    return (
        <Fragment>
            <div className="container-fluid p-3 pt-2 g-0 adma">
                <div className="row">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="section-title">
                                <h4 className="mt-1">CATEGORY MANAGEMENT</h4>
                                <p className="text-muted p-0 m-0">Manage your product categories and organization</p>
                            </div>
                            <button 
                                type="button" 
                                className="add-category-btn"
                                data-bs-toggle="modal" 
                                data-bs-target="#exampleModal"
                                aria-label="Add a new category"
                            >
                                <FaPlus className="icon" />
                                <span className="txt">Add Category</span>
                            </button>
                        </div>
                    </div>
                </div>

                <div className="pending-control-panel categories-control-panel">
                    <div className="search-control-wrapper flex-grow-1" style={{ maxWidth: '450px' }}>
                        <input
                            type="text"
                            className="form-control search-input-custom w-100"
                            placeholder="Search by category name..."
                            value={searchTerm}
                            onChange={handleSearchCategory}
                        />
                        <FaSearch className="search-icon-custom" />
                    </div>
                    <div className="d-flex align-items-center">
                        <small className="text-muted fw-semibold">
                            Showing {filteredCategories.length} of {categoryList.length} categories
                        </small>
                    </div>
                </div>

                {loading ? (
                    <LoadingScreen />
                ) : (
                    <div className="categories-grid-row">
                        {filteredCategories.length > 0 ? (
                            filteredCategories.map((data) => (
                                <div className="col-lg-3 col-md-4 col-sm-6" key={data._id}>
                                    <div className="category-card h-100" tabIndex={0} role="group" aria-label={`Category ${data.name}`}
                                         onKeyDown={(e)=> { if(e.key==='Enter'){ window.location.href=`/admin/categories/edit/${data._id}`; }}}>
                                        <div className="category-thumb">
                                            {data.image ? (
                                                <img
                                                    src={data.image.startsWith('http') ? data.image : `${API_BASE_URL}${data.image}`}
                                                    alt={data.name}
                                                    loading="lazy"
                                                    className={`category-img ${loadedImages[data._id] ? 'show' : ''}`}
                                                    onLoad={()=> setLoadedImages(prev=>({...prev,[data._id]:true}))}
                                                    onError={(e)=> { e.target.style.display='none'; }}
                                                />
                                            ) : (
                                                <div className="category-initial" aria-hidden="true">{data.name.charAt(0).toUpperCase()}</div>
                                            )}
                                            {!loadedImages[data._id] && data.image && <div className="category-skeleton" />}
                                            <div className="category-actions-bar">
                                                <Link to={`/admin/categories/edit/${data._id}`} className="category-action-btn edit" title="Edit Category" aria-label={`Edit ${data.name}`}>
                                                    <FaPen />
                                                </Link>
                                                <button type="button" className="category-action-btn delete" title="Delete Category" aria-label={`Delete ${data.name}`} onClick={()=>handleDeleteCategory(data._id)}>
                                                    <FaTrash />
                                                </button>
                                            </div>
                                        </div>
                                        <div className="category-body">
                                            <h6 className="category-name" title={data.name}>{data.name}</h6>
                                            <small className="text-muted">Category</small>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="col-12">
                                <div className="card border-0 shadow-sm">
                                    <div className="card-body text-center py-5">
                                        <FaFolder className="text-muted fs-1 mb-3" />
                                        <h5 className="text-muted">
                                            {searchTerm ? 'No categories found matching your search.' : 'No categories found'}
                                        </h5>
                                        <p className="text-muted">
                                            {searchTerm ? 'Try adjusting your search terms.' : 'Add your first category to get started.'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Add Category Modal */}
            <div className="modal fade add-category-modal" id="exampleModal" tabIndex={-1} aria-labelledby="exampleModalLabel" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title fw-bold">Add New Category</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
                        </div>
                        <div className="modal-body pt-0">
                            <div className="mb-3">
                                <label className="form-label fw-bold">Category Name</label>
                                <input 
                                    type="text" 
                                    className="form-control rounded-0" 
                                    placeholder="Enter category name" 
                                    name="name" 
                                    value={categoryData.name} 
                                    onChange={handleChange} 
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label fw-bold">Category Image</label>
                                <input 
                                    type="file" 
                                    className="form-control rounded-0" 
                                    accept="image/*" 
                                    onChange={handleImageUpload} 
                                />
                                {imageUploading && (
                                    <div className="mt-2">
                                        <FaSpinner className="spinner-border spinner-border-sm me-2" />
                                        <small className="text-muted">Uploading image...</small>
                                    </div>
                                )}
                                {categoryData.image && (
                                    <div className="mt-2 add-category-preview border">
                                        <div className="d-flex align-items-center justify-content-between px-2 py-1 bg-white border-bottom">
                                            <small className="text-success d-flex align-items-center m-0"><FaImage className="me-1" /> Preview</small>
                                            <small className="text-muted">{categoryData.name || 'New category'}</small>
                                        </div>
                                        <div className="p-2 text-center bg-light">
                                            <img src={categoryData.image && categoryData.image.startsWith('http') ? categoryData.image : `${API_BASE_URL}${categoryData.image}`} alt="Preview" className="img-fluid" style={{maxHeight:'140px',objectFit:'contain'}} />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="modal-footer pt-0">
                            <button type="button" className="btn btn-light rounded-0 border" data-bs-dismiss="modal">
                                Cancel
                            </button>
                            <button 
                                type="button" 
                                className="btn save-category-btn rounded-0" 
                                onClick={handleAddCategory} 
                                disabled={savingCategory || imageUploading || !categoryData.name.trim() || !categoryData.image.trim()}
                            >
                                {savingCategory ? (
                                    <>
                                        <FaSpinner className="spinner-border spinner-border-sm me-2" />
                                        Saving...
                                    </>
                                ) : (
                                    'Save Category'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Styles moved to global index.css under Category Management */}
        </Fragment>
    );
}

export default CategoryPage;
